// power/shell.jsx — the Power page: a full takeover, opened from the home
// card or the menu. Built from the 2026-10-22 canvas mockups.
//
// Two columns on the iPad. Left is now: the price with its verdict, today
// so far, and the live flows. Right is the picture over time: the price
// for the next 20 hours, the last 30 days in dollars, and where the power
// came from. A phone gets one column, price first.
//
// Dollars lead; kWh is a switch in the header (Ben, 2026-10-22: the family
// "will only really understand dollars"). Past days are Amber's settled
// figures; today is HA's estimate and says so. Bars are ink: colour is
// spent only on a price spike (rust, "needs you").

import * as React from 'react';
import { TakeoverShell, Card, fs, Segmented } from '../direction-a.jsx';
import { FONT } from '../theme.js';
import { Price, SpikeLine } from './card.jsx';
import { usePowerHistory } from './use-power.jsx';
import {
  FIXED_PER_DAY, sankeyFlows, totals, fmtClock, fmtRange, fmtMoney, fmtKwh, fmtDayShort, fmtDayLong, placeBrackets,
} from './model.js';

// Pixel width of a string in a CSS font, for laying out SVG labels. Falls
// back to a generous per-character estimate if canvas is unavailable.
let measureCtx;
function textWidth(font) {
  measureCtx = measureCtx || document.createElement('canvas').getContext('2d');
  return (t) => {
    if (!measureCtx) return t.length * parseFloat(font.split(' ')[1]) * 0.6;
    measureCtx.font = font;
    return measureCtx.measureText(t).width;
  };
}

function useWidth() {
  const ref = React.useRef(null);
  const [w, setW] = React.useState(0);
  React.useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const label = (T) => ({ fontSize: fs(T, 13), color: T.inkSoft });
const value = (T, size = 16) => ({ fontSize: fs(T, size), color: T.ink, fontWeight: 500, fontVariantNumeric: 'tabular-nums' });
const note = (T) => ({ fontSize: fs(T, 14), lineHeight: 1.45, color: T.inkSoft, margin: 0 });
const big = (T, size) => ({ fontFamily: FONT.serif, fontSize: size, fontWeight: 400, lineHeight: .9, color: T.ink, fontVariantNumeric: 'lining-nums tabular-nums' });
const Hair = ({ T }) => <div style={{ height: 1, background: T.hair }}/>;

function Fact({ T, k, v }) {
  if (v == null) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span style={label(T)}>{k}</span>
      <span style={value(T)}>{v}</span>
    </div>
  );
}

// ── Price now ──────────────────────────────────────────────────────

function PriceCard({ T, power, viewport }) {
  const { price, advice } = power;
  const spike = advice?.kind === 'spike';
  return (
    <Card T={T} eyebrow={spike ? null : 'Price now'}
      headerAction={<span style={label(T)}>Amber{price.start && price.end ? `, ${fmtClock(price.start)} to ${fmtClock(price.end)}` : ''}</span>}>
      {spike && <SpikeLine T={T} text="Price spike"/>}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <Price T={T} cents={price.cents} size={viewport === 'phone' ? 64 : 76}/>
        <span style={{ fontSize: fs(T, 15), color: T.inkSoft }}>per kWh</span>
      </div>
      {advice && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {!spike && <div style={{ fontSize: fs(T, 18), fontWeight: 600 }}>{advice.headline}</div>}
          <p style={{ ...note(T), fontSize: fs(T, 15), color: spike ? T.ink : T.inkSoft }}>{advice.detail}</p>
        </div>
      )}
      <Hair T={T}/>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 20, rowGap: 14 }}>
        <Fact T={T} k={advice?.cheap?.tomorrow ? 'Cheapest tomorrow' : 'Cheapest'} v={advice?.cheap ? `${fmtRange({ ...advice.cheap, tomorrow: false })}, ${Math.round(advice.cheap.avg)}c` : null}/>
        <Fact T={T} k={advice?.dear?.tomorrow ? 'Dearest tomorrow' : 'Dearest'} v={advice?.dear ? `${fmtRange({ ...advice.dear, tomorrow: false })}, ${Math.round(advice.dear.avg)}c` : null}/>
        <Fact T={T} k="Selling to the grid" v={price.feedInCents != null ? `${Math.round(price.feedInCents)}c per kWh` : null}/>
        <Fact T={T} k="Grid is renewable" v={price.renewables != null ? `${Math.round(price.renewables)}%` : null}/>
      </div>
    </Card>
  );
}

// ── Next 20 hours ──────────────────────────────────────────────────

// Night from sun.sun, for the quiet band behind the bars.
function nightSpans(states, from, to) {
  const s = states?.['sun.sun']?.attributes;
  const rise = s?.next_rising ? new Date(s.next_rising) : null;
  const set = s?.next_setting ? new Date(s.next_setting) : null;
  if (!rise || !set) return [];
  // The chart runs 38 hours, so it can hold a second night after the first.
  const day = 86400000, later = (d) => new Date(d.getTime() + day);
  const spans = set < rise
    ? [[set, rise], [later(set), later(rise)]]                  // daytime now
    : [[from, rise], [set, later(rise)]];                       // night now
  return spans
    .map(([a, b]) => [a < from ? from : a, b > to ? to : b])
    .filter(([a, b]) => b > a);
}

function ForecastChart({ T, power, states }) {
  const [ref, width] = useWidth();
  const { slots, advice } = power;
  const n = slots.length;
  const H = 196, top = 30, barH = 130, labelY = 182;
  const ok = width > 0 && n > 1;
  const step = ok ? width / n : 0;
  const peak = Math.max(...slots.map((s) => Math.max(s.cents, s.high ?? 0)), 35);
  const ceil = Math.min(Math.ceil(peak / 10) * 10, 100);       // spikes past $1 clip
  const y = (c) => top + barH - (Math.max(0, Math.min(c, ceil)) / ceil) * barH;
  const t0 = n ? slots[0].start.getTime() : 0, t1 = n ? slots[n - 1].end.getTime() : 1;
  const x = (d) => ((d.getTime() - t0) / (t1 - t0)) * width;
  const inWin = (s, w) => w && s.start >= w.start && s.end <= w.end;
  const narrow = width < 420;
  const gstep = ceil > 50 ? 25 : 10;
  const grid = [];
  for (let c = gstep; c < ceil; c += gstep) grid.push(c);
  // Hour labels every three hours, or six or twelve when three-hourly labels
  // would run together ("9 pmmidnight3 am" on a laptop window), and none close
  // enough to collide with "Now" (at 11:30 the noon tick sat on top of it).
  const tickW = ok ? Math.max(...['midnight', '10 pm', 'noon'].map(textWidth(`${fs(T, 13)}px ${FONT.sans}`))) + 10 : 0;
  const every = [3, 6, 12].find((h) => h * 2 * step >= tickW) || 12;
  const ticks = slots.map((s, i) => ({ s, i })).filter(({ s, i }) => i * step >= 46 && s.start.getMinutes() === 0 && s.start.getHours() % every === 0);
  const sans = { fontFamily: FONT.sans };
  const wins = advice?.kind === 'spike' ? [] : [['Dearest', advice?.dear], ['Cheapest', advice?.cheap]]
    .filter(([, w]) => w)
    .map(([word, w]) => ({ w, a: x(w.start), b: x(w.end), short: word, full: narrow ? word : `${word} ${fmtRange(w)}` }));
  const placed = ok ? placeBrackets(wins, width, textWidth(`500 ${fs(T, 12)}px ${FONT.sans}`)) : [];
  return (
    <div ref={ref} style={{ width: '100%' }}>
      {ok && (
        <svg width={width} height={H} role="img" style={{ display: 'block', overflow: 'visible' }}
          aria-label={`Amber's predicted price for the next ${Math.round((t1 - t0) / 3600000)} hours, ${Math.round(Math.min(...slots.map((s) => s.cents)))} to ${Math.round(Math.max(...slots.map((s) => s.cents)))} cents per kWh`}>
          {nightSpans(states, slots[0].start, slots[n - 1].end).map(([a, b], i) => (
            <rect key={`n${i}`} x={x(a)} y={top} width={x(b) - x(a)} height={barH} fill={T.cardSoft}/>
          ))}
          {wins.map(({ a, b, short }, i) => (
            <g key={short}>
              <path d={`M${a + 1} 28 V24 H${b - 1} V28`} fill="none" stroke={T.ink} strokeWidth={1.2}/>
              <text x={placed[i].x} y={16} textAnchor="middle" fontSize={fs(T, 12)} fontWeight={500} fill={T.ink} style={sans}>{placed[i].text}</text>
            </g>
          ))}
          {grid.map((c) => <line key={c} x1={0} x2={width} y1={y(c)} y2={y(c)} stroke={T.hair}/>)}
          {/* How dear it could get: a pale cap from Amber's prediction up to
              the top of its likely range. The low end is left off; drawn
              behind the bar it muddied every column. */}
          {slots.map((s, i) => (s.high != null && s.high > s.cents && !s.spike ? (
            <rect key={`r${i}`} x={i * step + step * 0.15} y={y(s.high)} width={step * 0.7} height={Math.max(0, y(s.cents) - y(s.high))} rx={1.5}
              fill={T.ink} fillOpacity={0.1}/>
          ) : null))}
          {slots.map((s, i) => {
            const strong = i === 0 || inWin(s, advice?.cheap);
            return (
              <rect key={i} x={i * step + step * 0.15} y={y(s.cents)} width={step * 0.7} height={top + barH - y(s.cents)} rx={1.5}
                fill={s.spike ? T.attention : T.ink} fillOpacity={s.spike || strong ? 1 : 0.26}/>
            );
          })}
          <line x1={0} x2={width} y1={top + barH} y2={top + barH} stroke={T.hairHi}/>
          {grid.map((c) => (
            <text key={`l${c}`} x={2} y={y(c) - 4} fontSize={fs(T, 11)} fill={T.mute} stroke={T.card} strokeWidth={3} paintOrder="stroke" style={sans}>{c}c</text>
          ))}
          <text x={0} y={labelY} fontSize={fs(T, 13)} fontWeight={600} fill={T.ink} style={sans}>Now</text>
          {ticks.map(({ s, i }) => (
            <text key={`t${i}`} x={i * step} y={labelY} textAnchor="middle" fontSize={fs(T, 13)} fill={T.inkSoft} style={sans}>{fmtClock(s.start)}</text>
          ))}
        </svg>
      )}
    </div>
  );
}

// ── Today so far ───────────────────────────────────────────────────

function TodayCard({ T, today, history, unit }) {
  if (!today) return null;
  const kwh = unit === 'kwh';
  const rows = [
    ['Bought from the grid', fmtKwh(today.boughtKwh), fmtMoney(today.cost)],
    ['Sold to the grid', fmtKwh(today.soldKwh), fmtMoney(today.earned)],
    ['Daily charges', '', fmtMoney(today.fixed)],
  ];
  const y = history?.yesterday, typical = history?.typical;
  return (
    <Card T={T} eyebrow="Today so far" headerAction={<span style={label(T)}>estimate</span>}>
      <span style={big(T, 64)}>{kwh ? fmtKwh(today.boughtKwh) : today.net < 0 ? `+${fmtMoney(-today.net)}` : fmtMoney(today.net)}</span>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', columnGap: 18, rowGap: 10, alignItems: 'baseline', fontSize: fs(T, 15), fontVariantNumeric: 'tabular-nums' }}>
        {rows.map(([k, a, b]) => (
          <React.Fragment key={k}>
            <span style={{ color: T.inkSoft }}>{k}</span>
            <span style={{ color: kwh ? T.ink : T.mute, fontSize: fs(T, kwh ? 15 : 13), fontWeight: kwh ? 500 : 400, textAlign: 'right' }}>{a}</span>
            <span style={{ color: kwh ? T.mute : T.ink, fontSize: fs(T, kwh ? 13 : 15), fontWeight: kwh ? 400 : 500, textAlign: 'right' }}>{b}</span>
          </React.Fragment>
        ))}
      </div>
      {(y || typical != null) && (
        <>
          <Hair T={T}/>
          <div style={{ fontSize: fs(T, 14), color: T.inkSoft }}>
            {y && <>Yesterday <span style={{ color: T.ink, fontWeight: 500 }}>{kwh ? fmtKwh(y.bought) : fmtMoney(y.net)}</span></>}
            {y && typical != null && ' · '}
            {typical != null && !kwh && <>A normal day <span style={{ color: T.ink, fontWeight: 500 }}>{fmtMoney(typical)}</span></>}
          </div>
        </>
      )}
    </Card>
  );
}

// ── Right now ──────────────────────────────────────────────────────

function NowCard({ T, now, viewport }) {
  const kw = (v) => (v == null ? '–' : `${Math.abs(v).toFixed(1)} kW`);
  const grid = now.gridKw;
  const tiles = [
    ['Solar', kw(now.solarKw), 'making'],
    ['House', kw(now.houseKw), 'using'],
    ['Grid', kw(grid), grid == null ? '' : grid < -0.05 ? 'selling' : grid > 0.05 ? 'buying' : 'idle'],
    ['Battery', now.battery?.level ?? '–', [now.battery?.doing, now.capacityKwh ? `${Math.round(now.capacityKwh)} kWh battery` : null].filter(Boolean).join(', ')],
  ];
  const phone = viewport === 'phone';
  return (
    <Card T={T} eyebrow="Right now">
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${phone ? 2 : 4}, minmax(0, 1fr))`, gap: phone ? 10 : 12 }}>
        {tiles.map(([k, v, s]) => (
          <div key={k} style={{ background: T.cardSoft, borderRadius: 10, padding: '12px 12px 14px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={label(T)}>{k}</span>
            <span style={{ ...value(T, 20), fontWeight: 600 }}>{v}</span>
            {s && <span style={{ fontSize: fs(T, 12), color: T.mute }}>{s}</span>}
          </div>
        ))}
      </div>
      {now.solarToday != null && (
        <p style={note(T)}>
          Solar is forecast to make {Math.round(now.solarToday)} kWh today
          {now.solarPeakAt && now.solarPeakKw != null ? `, peaking at ${now.solarPeakKw.toFixed(1)} kW around ${fmtClock(now.solarPeakAt)}` : ''}.
        </p>
      )}
    </Card>
  );
}

// ── Last 30 days ───────────────────────────────────────────────────

function MonthChart({ T, days, unit, standout }) {
  const [ref, width] = useWidth();
  const n = days.length;
  const ok = width > 0 && n > 1;
  const kwh = unit === 'kwh';
  const v = (d) => (kwh ? d.bought : d.net);
  const pos = days.map(v).filter((x) => x > 0);
  const ceil = Math.max(10, Math.ceil(Math.max(...pos, 0) / 10) * 10);
  const zero = 10 + 120, ph = 120, below = 30;
  const step = ok ? width / n : 0, bw = step * 0.62;
  const y = (x) => zero - (x / ceil) * ph;
  const grid = [];
  for (let g = ceil / (ceil > 30 ? 4 : 3); g <= ceil + 0.01; g += ceil / (ceil > 30 ? 4 : 3)) grid.push(Math.round(g));
  const sans = { fontFamily: FONT.sans };
  const marks = [0, Math.round((n - 1) / 3), standout ? days.indexOf(standout) : Math.round((2 * (n - 1)) / 3), n - 1]
    .filter((i, k, a) => i >= 0 && a.indexOf(i) === k);
  return (
    <div ref={ref} style={{ width: '100%' }}>
      {ok && (
        <svg width={width} height={200} role="img" style={{ display: 'block', overflow: 'visible' }}
          aria-label={`${kwh ? 'Grid power bought' : 'Cost'} each day for the last ${n} days`}>
          {grid.map((g) => <line key={g} x1={0} x2={width} y1={y(g)} y2={y(g)} stroke={T.hair}/>)}
          {days.map((d, i) => {
            const val = v(d);
            const x0 = i * step + (step - bw) / 2;
            if (val >= 0) return <rect key={d.date} x={x0} y={y(val)} width={bw} height={zero - y(val)} rx={1.5} fill={T.ink} fillOpacity={0.72}/>;
            return (
              <g key={d.date}>
                <rect x={x0} y={zero} width={bw} height={below} rx={1.5} fill={T.ink}/>
                <path d={`M${x0 - 2} ${zero + 18} L${x0 + bw + 2} ${zero + 13} M${x0 - 2} ${zero + 23} L${x0 + bw + 2} ${zero + 18}`} stroke={T.card} strokeWidth={2.2}/>
                <text x={x0 + bw + 6} y={zero + 28} fontSize={fs(T, 13)} fontWeight={600} fill={T.ink} style={sans}>+{fmtMoney(-val, { whole: true })} for the day</text>
              </g>
            );
          })}
          <line x1={0} x2={width} y1={zero} y2={zero} stroke={T.hairHi}/>
          {grid.map((g) => (
            <text key={`l${g}`} x={2} y={y(g) - 4} fontSize={fs(T, 11)} fill={T.mute} stroke={T.card} strokeWidth={3} paintOrder="stroke" style={sans}>{kwh ? `${g} kWh` : `$${g}`}</text>
          ))}
          {marks.map((i, k) => (
            <text key={`d${i}`} x={k === 0 ? 0 : i === n - 1 ? width : i * step + step / 2} y={190}
              textAnchor={k === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fontSize={fs(T, 13)} fill={T.inkSoft} style={sans}>
              {fmtDayShort(days[i].date)}
            </text>
          ))}
        </svg>
      )}
    </div>
  );
}

function MonthCard({ T, history, unit }) {
  if (!history) return <Card T={T} eyebrow="Last 30 days"><div style={{ height: 200 }}/></Card>;
  const { days } = history;
  if (!days.length) {
    return (
      <Card T={T} eyebrow="Last 30 days">
        <p style={note(T)}>Amber's settled figures aren't in Home Assistant yet. They arrive each morning for the day before.</p>
      </Card>
    );
  }
  const t = history.totals, kwh = unit === 'kwh';
  const s = history.standout;
  return (
    <Card T={T} eyebrow={`Last ${days.length} days`} headerAction={<span style={label(T)}>{fmtDayShort(days[0].date)} to {fmtDayShort(days[days.length - 1].date)}</span>}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
        <span style={big(T, 64)}>{kwh ? fmtKwh(t.bought) : fmtMoney(t.net)}</span>
        <span style={{ fontSize: fs(T, 15), color: T.inkSoft }}>
          {kwh ? 'bought from the grid' : t.earned >= 1 ? `after ${fmtMoney(t.earned, { whole: true })} of power sold back` : 'including daily charges'}
        </span>
      </div>
      {!kwh && (
        <p style={{ ...note(T), fontSize: fs(T, 15) }}>
          A normal day costs about {fmtMoney(history.typical, { whole: true })}.
          {s && ` On ${fmtDayLong(s.date)} the battery sold ${Math.round(s.sold)} kWh during a price spike and earned ${fmtMoney(s.earned, { whole: true })}.`}
        </p>
      )}
      <MonthChart T={T} days={days} unit={unit} standout={s}/>
      <Hair T={T}/>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16, fontVariantNumeric: 'tabular-nums' }}>
        {[
          ['Bought', kwh ? fmtKwh(t.bought) : fmtMoney(t.cost, { whole: true }), kwh ? fmtMoney(t.cost, { whole: true }) : fmtKwh(t.bought)],
          ['Sold', kwh ? fmtKwh(t.sold) : fmtMoney(t.earned, { whole: true }), kwh ? fmtMoney(t.earned, { whole: true }) : fmtKwh(t.sold)],
          ['Daily charges', fmtMoney(t.fixed, { whole: true }), `${fmtMoney(FIXED_PER_DAY)} a day`],
        ].map(([k, a, b]) => (
          <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={label(T)}>{k}</span>
            <span style={{ ...value(T, 18), fontWeight: 600 }}>{a}</span>
            <span style={{ fontSize: fs(T, 12), color: T.mute }}>{b}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Where it came from ─────────────────────────────────────────────

const SOURCE_FILL = (T) => ({ solar: T.isDark ? '#6F6B65' : '#B9B5AE', grid: T.ink, battery: T.mute });

function SankeyChart({ T, flows, unit }) {
  const [ref, width] = useWidth();
  const H = 240, pad = 20, gap = 12, nodeW = 10, hubW = 12;
  const ok = width > 0;
  const sumIn = flows.sources.reduce((s, f) => s + f.kwh, 0);
  const sumOut = flows.uses.reduce((s, f) => s + f.kwh, 0);
  const total = Math.max(sumIn, sumOut, 1);
  const sc = (H - 2 * pad - 2 * gap) / total;
  const phone = width < 400;
  const lx = phone ? 70 : Math.round(width * 0.24);           // left node
  const hx = Math.round(width / 2 - hubW / 2);                // hub
  const rx = width - (phone ? 84 : Math.round(width * 0.24)) - nodeW; // right node
  const stack = (items, startY, g) => {
    let yy = startY;
    return items.map((f) => { const h = Math.max(f.kwh * sc, 1.5); const r = { ...f, y0: yy, y1: yy + h }; yy += h + g; return r; });
  };
  const L = stack(flows.sources, pad, gap), R = stack(flows.uses, pad, gap);
  const hubTop = H / 2 - (sumIn * sc) / 2, hubTopR = H / 2 - (sumOut * sc) / 2;
  const HL = stack(flows.sources, hubTop, 0), HR = stack(flows.uses, hubTopR, 0);
  const band = (xa, a0, a1, xb, b0, b1) => {
    const m = (xa + xb) / 2;
    return `M${xa},${a0} C${m},${a0} ${m},${b0} ${xb},${b0} L${xb},${b1} C${m},${b1} ${m},${a1} ${xa},${a1} Z`;
  };
  const fill = SOURCE_FILL(T);
  const sans = { fontFamily: FONT.sans };
  const kwhLine = (f) => `${Math.round(f.kwh).toLocaleString('en-AU')} kWh`;
  const dollarLine = (f) => (unit === 'dollars' && f.dollars != null && f.dollars >= 0.5 ? fmtMoney(f.dollars, { whole: true }) : null);
  return (
    <div ref={ref} style={{ width: '100%' }}>
      {ok && (
        <svg width={width} height={H + 34} role="img" style={{ display: 'block', overflow: 'visible' }}
          aria-label={`Power in: ${flows.sources.map((f) => `${f.label} ${Math.round(f.kwh)} kWh`).join(', ')}. Power out: ${flows.uses.map((f) => `${f.label} ${Math.round(f.kwh)} kWh`).join(', ')}.`}>
          {L.map((f, i) => <path key={`l${f.key}`} d={band(lx + nodeW, f.y0, f.y1, hx, HL[i].y0, HL[i].y1)} fill={fill[f.key]} fillOpacity={f.key === 'grid' ? 0.18 : f.key === 'solar' ? 0.5 : 0.3}/>)}
          {R.map((f, i) => <path key={`r${f.key}`} d={band(hx + hubW, HR[i].y0, HR[i].y1, rx, f.y0, f.y1)} fill={T.ink} fillOpacity={0.1}/>)}
          {L.map((f) => <rect key={`nl${f.key}`} x={lx} y={f.y0} width={nodeW} height={f.y1 - f.y0} fill={fill[f.key]}/>)}
          <rect x={hx} y={Math.min(hubTop, hubTopR)} width={hubW} height={total * sc} fill={T.inkSoft}/>
          {R.map((f) => <rect key={`nr${f.key}`} x={rx} y={f.y0} width={nodeW} height={f.y1 - f.y0} fill={f.key === 'battery' ? T.mute : T.inkSoft}/>)}
          {L.map((f) => {
            const c = (f.y0 + f.y1) / 2;
            return (
              <g key={`tl${f.key}`} textAnchor="end" style={sans}>
                <text x={lx - 10} y={c - 3} fontSize={fs(T, 14)} fontWeight={600} fill={T.ink}>{f.label}</text>
                <text x={lx - 10} y={c + 13} fontSize={fs(T, 12)} fill={T.inkSoft}>{kwhLine(f)}</text>
                {dollarLine(f) && <text x={lx - 10} y={c + 28} fontSize={fs(T, 12)} fill={T.inkSoft}>{dollarLine(f)}</text>}
              </g>
            );
          })}
          {R.map((f, i) => {
            const c = (f.y0 + f.y1) / 2;
            const prev = i ? (R[i - 1].y0 + R[i - 1].y1) / 2 : -Infinity;
            const cy = Math.max(c, prev + (i && dollarLine(R[i - 1]) ? 50 : 38));
            return (
              <g key={`tr${f.key}`} style={sans}>
                <text x={rx + nodeW + 10} y={cy - 3} fontSize={fs(T, 14)} fontWeight={600} fill={T.ink}>{f.label}</text>
                <text x={rx + nodeW + 10} y={cy + 13} fontSize={fs(T, 12)} fill={T.inkSoft}>{kwhLine(f)}</text>
                {dollarLine(f) && <text x={rx + nodeW + 10} y={cy + 28} fontSize={fs(T, 12)} fill={T.inkSoft}>{dollarLine(f)}</text>}
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

function SankeyCard({ T, history, unit, viewport }) {
  const [period, setPeriod] = React.useState('month');
  if (!history?.days?.length) return null;
  const yday = history.yesterday;
  const usePeriod = period === 'day' && yday ? 'day' : 'month';
  const t = usePeriod === 'day' ? totals([yday]) : history.totals;
  const flows = sankeyFlows(t);
  const solarShare = t.bought + t.solar + t.batteryOut > 0 ? t.solar / (t.bought + t.solar + t.batteryOut) : 0;
  return (
    <Card T={T} eyebrow={viewport === 'phone' ? 'Where it came from' : 'Where our power came from'}
      headerAction={yday ? (
        <Segmented T={T} ariaLabel="Period" height={32} fontSize={13} stretch={false}
          options={[{ value: 'day', label: 'Yesterday' }, { value: 'month', label: `${history.days.length} days` }]}
          value={usePeriod} onChange={setPeriod}/>
      ) : null}>
      <SankeyChart T={T} flows={flows} unit={unit}/>
      <p style={note(T)}>
        The house used {Math.round(t.house).toLocaleString('en-AU')} kWh. {Math.round(solarShare * 100)}% of the power that came in was solar; the battery stored {Math.round(t.batteryIn).toLocaleString('en-AU')} kWh and gave back {Math.round(t.batteryOut).toLocaleString('en-AU')}.
      </p>
    </Card>
  );
}

// ── The page ───────────────────────────────────────────────────────

// Mounted only while open, so the switches reset on close.
export function PowerShell({ open, ...props }) {
  if (!open) return null;
  return <PowerPage {...props}/>;
}

function PowerPage({ T, onClose, power, viewport, hass }) {
  const [unit, setUnit] = React.useState('dollars');
  const history = usePowerHistory(hass, true);
  const phone = viewport === 'phone';

  const unitSwitch = (
    <Segmented T={T} ariaLabel="Show amounts in" height={phone ? 38 : 40} fontSize={phone ? 14 : 15} stretch={false}
      options={[{ value: 'dollars', label: phone ? '$' : 'Dollars' }, { value: 'kwh', label: 'kWh' }]}
      value={unit} onChange={setUnit}/>
  );

  const price = power.ready ? <PriceCard T={T} power={power} viewport={viewport}/> : (
    <Card T={T}>
      <div style={{ fontSize: fs(T, 16), fontWeight: 500 }}>Power prices unavailable</div>
      <p style={note(T)}>Amber's prices aren't reaching Home Assistant, so nothing is shown rather than an old price.</p>
    </Card>
  );
  const forecast = power.slots.length > 1 && (
    <Card T={T} eyebrow={`Price, next ${Math.round((power.slots[power.slots.length - 1].end - power.slots[0].start) / 3600000)} hours`}
      headerAction={<span style={label(T)}>Amber's prediction, cents per kWh</span>}>
      <ForecastChart T={T} power={power} states={hass?.states}/>
    </Card>
  );
  const today = <TodayCard T={T} today={power.today} history={history} unit={unit}/>;
  const month = <MonthCard T={T} history={history} unit={unit}/>;
  const sankey = <SankeyCard T={T} history={history} unit={unit} viewport={viewport}/>;
  const now = <NowCard T={T} now={power.now} viewport={viewport}/>;

  const body = phone ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {price}{forecast}{today}{month}{sankey}{now}
    </div>
  ) : (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.25fr)', gap: 20, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>{price}{today}{now}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>{forecast}{month}{sankey}</div>
    </div>
  );

  return (
    <TakeoverShell T={T} viewport={viewport} eyebrow="Wallace St" title="Power" onClose={onClose} headerAction={unitSwitch} bodyless>
      <div style={{
        padding: phone ? '8px 18px calc(32px + env(safe-area-inset-bottom))' : '12px 36px calc(40px + env(safe-area-inset-bottom))',
        maxWidth: 1180, margin: '0 auto', boxSizing: 'border-box',
      }}>
        {body}
        <div style={{ marginTop: 18, fontSize: fs(T, 12), lineHeight: 1.45, color: T.mute, textAlign: 'center' }}>
          Past days are Amber's settled figures, plus {fmtMoney(FIXED_PER_DAY)} a day in supply and membership charges. Today is Home Assistant's estimate until Amber settles it the next morning.
        </div>
      </div>
    </TakeoverShell>
  );
}
